import type {
  AssignmentDetail,
  AttendanceDetail,
  AuditEntry,
  RegistrationAwaitingDecision,
  Volunteer,
  VolunteerListItem,
  VolunteerStatus,
} from '@parinaam-vms/shared';
import { apiDelete, apiGet, apiGetPage, apiPatch, apiPost, toQueryString } from './client';
import type { Page } from './client';

/** A `type` rather than an `interface`, so it can be passed to `toQueryString`. */
export type VolunteerListQuery = {
  status?: string;
  search?: string;
  page?: number;
  page_size?: number;
};

export function fetchVolunteers(params: VolunteerListQuery): Promise<Page<VolunteerListItem>> {
  return apiGetPage<VolunteerListItem>(`/volunteers${toQueryString(params)}`);
}

export function fetchVolunteer(id: string): Promise<Volunteer> {
  return apiGet<Volunteer>(`/volunteers/${id}`);
}

/** The audit trail for one volunteer, newest first (M2, §11.53). */
export function fetchVolunteerHistory(id: string): Promise<AuditEntry[]> {
  return apiGet<AuditEntry[]>(`/volunteers/${id}/history`);
}

export function fetchVolunteerAssignments(id: string): Promise<AssignmentDetail[]> {
  return apiGet<AssignmentDetail[]>(`/volunteers/${id}/assignments`);
}

export function fetchVolunteerAttendance(id: string): Promise<AttendanceDetail[]> {
  return apiGet<AttendanceDetail[]>(`/volunteers/${id}/attendance`);
}

/**
 * The editable fields.
 *
 * A key that is absent leaves the column alone; a key set to `null` clears it.
 * The edit form therefore sends only what it displays, and cannot blank a field
 * it never rendered.
 */
export type VolunteerUpdateBody = Partial<
  Pick<
    Volunteer,
    | 'first_name'
    | 'middle_name'
    | 'last_name'
    | 'preferred_name'
    | 'email'
    | 'phone'
    | 'alternate_phone'
    | 'age_group'
    | 'gender'
    | 'address_line_1'
    | 'address_line_2'
    | 'city'
    | 'state'
    | 'postal_code'
    | 'country'
    | 'occupation'
    | 'occupation_other'
    | 'organization'
    | 'education'
    | 'skills'
    | 'languages'
    | 'areas_of_interest'
    | 'category'
    | 'organization_id'
    | 'contact_preference'
    | 'training_completed_on'
    | 'status'
  >
> & {
  /**
   * Why the status changed. Not a column — it lands on the audit row, and on the
   * registration this volunteer came from if that is still open.
   *
   * Required by the API whenever `status` becomes `REJECTED`, from this screen
   * or from the review queue: `registration_requests_rejected_has_reason`
   * refuses an unexplained rejection whichever door it arrived through.
   */
  reason?: string;
};

export function updateVolunteer(id: string, body: VolunteerUpdateBody): Promise<Volunteer> {
  return apiPatch<Volunteer>(`/volunteers/${id}`, body);
}

/**
 * What `POST /volunteers` is sent.
 *
 * The editable set, plus the four fields the public registration form asks for
 * that the profile editor does not yet render — `institution` and
 * `referral_source` are recorded at intake and never revised, and the frequency
 * pair arrived with the registration spec. `createVolunteerSchema` has always
 * accepted them (it spreads the same `personalDetailsShape` the intake uses);
 * the staff form simply had no way to say them, so a volunteer entered by hand
 * came in thinner than one who filled the form in themselves.
 *
 * `reason` is excluded: it explains a status change, and a record being created
 * has not changed status.
 */
export type VolunteerCreateBody = Omit<VolunteerUpdateBody, 'reason'> &
  Partial<
    Pick<
      Volunteer,
      'institution' | 'referral_source' | 'volunteering_frequency' | 'volunteering_frequency_other'
    >
  >;

export function createVolunteer(body: VolunteerCreateBody): Promise<Volunteer> {
  return apiPost<Volunteer>('/volunteers', body);
}

/**
 * Destroy a volunteer record — Plan D12, and the only delete in the application.
 *
 * The API refuses unless the record is `PENDING` or `REJECTED` with no
 * assignments and no attendance, so the screen should only ever offer this on a
 * record that qualifies. When it does refuse, the 409 names what is in the way.
 */
export function deleteVolunteer(id: string, reason: string): Promise<{ deleted: true }> {
  return apiDelete<{ deleted: true }>(`/volunteers/${id}`, { reason });
}

/**
 * The submissions behind this volunteer (Plan D12).
 *
 * Usually none — a staff-entered record — or one. Several means they registered
 * more than once, and the repeats carry the duplicate flag that says so.
 */
export function fetchVolunteerRegistrations(id: string): Promise<RegistrationAwaitingDecision[]> {
  return apiGet<RegistrationAwaitingDecision[]>(`/volunteers/${id}/registrations`);
}

/**
 * The four status operations.
 *
 * Named, rather than a `status` field on the update body, because each is a
 * decision rather than a correction: it settles the registration behind the
 * person (D11), fires `volunteer.activated` to n8n, and writes its own audit
 * row. `VolunteerUpdateBody` deliberately has no `status` key, so the edit form
 * cannot reach them by accident.
 *
 * **`reopen` is the only route back to `PENDING`**, and only from `REJECTED`.
 * Nothing returns an *approved* volunteer to undecided, and the API refuses it
 * with `INVALID_STATE_TRANSITION` if a stale client tries.
 */
export function activateVolunteer(id: string, reason?: string): Promise<Volunteer> {
  return apiPost<Volunteer>(`/volunteers/${id}/activate`, reason ? { reason } : {});
}

export function deactivateVolunteer(id: string, reason?: string): Promise<Volunteer> {
  return apiPost<Volunteer>(`/volunteers/${id}/deactivate`, reason ? { reason } : {});
}

/** The reason is required — the API and the database both insist on it. */
export function rejectVolunteer(id: string, reason: string): Promise<Volunteer> {
  return apiPost<Volunteer>(`/volunteers/${id}/reject`, { reason });
}

/**
 * Set the status outright — admin only, and the API returns 403 to anybody else.
 *
 * Takes the destination, and accepts any of them. The server checks this one
 * route against neither the transition map nor the training gate, so the screen
 * offers every status rather than a filtered list.
 */
export function setVolunteerStatus(
  id: string,
  status: VolunteerStatus,
  reason: string
): Promise<Volunteer> {
  return apiPost<Volunteer>(`/volunteers/${id}/status`, { status, reason });
}
